import { Badge } from "@repo/components/src/ui/badge";
import { ComponentProps } from "react";

export type ClassificationTier = { maxFollowers: number; tiers: number[] };

type ClassificationBadgeProps = Pick<
  ComponentProps<typeof Badge>,
  "action" | "className"
> & { text: string };

// Index 0..4 map to VERY_BAD..VERY_GOOD; anything outside (a top "suspiciously
// high" bucket or an out-of-range follower count) falls back to SUSPECTED.
const BADGE_BY_TIER: ClassificationBadgeProps[] = [
  { action: "error", text: "Muy malo" },
  { action: "error", text: "Malo" },
  { action: "muted", text: "Normal" },
  { action: "success", text: "Bueno" },
  { action: "success", text: "Excelente" },
];
const SUSPECTED_BADGE: ClassificationBadgeProps = {
  action: "warning",
  text: "Sospechoso",
};

// Picks the follower bracket, then the first tier the value falls under, and
// maps that tier index to a badge. Engagement tables carry a 6th (Infinity)
// tier so unusually high values resolve to SUSPECTED; reach tables stop at the
// 5th tier so their top bucket is VERY_GOOD — both handled by this same lookup.
function classifyBadge(
  value: number,
  followers: number,
  brackets: ClassificationTier[],
): ClassificationBadgeProps {
  const bracket = brackets.find((b) => followers < b.maxFollowers);
  if (!bracket) return SUSPECTED_BADGE;
  const tierIndex = bracket.tiers.findIndex((t) => value < t);
  return BADGE_BY_TIER[tierIndex] ?? SUSPECTED_BADGE;
}

// Engagement tiers vary per platform, so the bracket table is injected.
export const getEngagementBadgeProps = (
  engagement: number,
  followers: number,
  engagementTiers: ClassificationTier[],
): ClassificationBadgeProps =>
  classifyBadge(engagement, followers, engagementTiers);

// Reach tiers are platform-agnostic.
const REACH_TIERS: ClassificationTier[] = [
  { maxFollowers: 5000, tiers: [90, 270, 450, 720, Infinity] },
  { maxFollowers: 50000, tiers: [600, 1800, 3000, 4800, Infinity] },
  { maxFollowers: 100000, tiers: [1313, 3938, 6563, 10500, Infinity] },
  { maxFollowers: 1000000, tiers: [6875, 20625, 34375, 55000, Infinity] },
  { maxFollowers: Infinity, tiers: [20000, 60000, 100000, 160000, Infinity] },
];

export const getReachBadgeProps = (
  reach: number,
  followers: number,
): ClassificationBadgeProps => classifyBadge(reach, followers, REACH_TIERS);
